import React, { useEffect } from 'react';
import { withTranslation } from 'config/i18n';
import { makeStyles, Box } from '@material-ui/core';
import WelcomeBanner from 'components/ListYourBike/WelcomeBanner';
import HowRentingWorks from 'components/ListYourBike/HowRentingWorks';
// import InsuranceInfo from 'components/ListYourBike/InsuranceInfo';
import QuestionsSection from 'components/ListYourBike/QuestionsSection';
import GotYourBack from 'components/ListYourBike/GotYourBack';
import WhyEagleShare from 'components/ListYourBike/WhyEagleShare';
import ReadyToEarn from 'components/ListYourBike/ReadyToEarn';
import { serverFetch } from 'utils/serverFetch';
import Head from 'next/head';
import { API } from 'config/axios';
import getConfig from 'next/config';
import { hotjar } from 'react-hotjar';
import generateHreflang from 'utils/generateHreflang';
import isCrawlers from 'common/utils/isCrawlers';
import LazyLoad from 'common/components/LazyLoad';
import generateMetaData from 'utils/generateMetaData';

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.only('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  bannerContainer: {
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.up('lg')]: {
      paddingTop: theme.spacing(14),
    },
  },
  // insuranceContainer: {
  //   paddingTop: theme.spacing(13),
  //   [theme.breakpoints.up('lg')]: {
  //     paddingTop: theme.spacing(22),
  //   },
  //   [theme.breakpoints.only('sm')]: {
  //     paddingTop: theme.spacing(5.5),
  //   },
  // },
  howRentingWorksContainer: {
    marginBottom: theme.spacing(13),
    paddingTop: theme.spacing(14),
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(26),
      paddingTop: theme.spacing(22),
    },
    [theme.breakpoints.only('sm')]: {
      marginBottom: theme.spacing(5.5),
      paddingTop: theme.spacing(9),
    },
  },
}));

const ListYourBike = ({
  t, seoValues, headLinks, ssrRender,
}) => {
  const classes = useStyles();
  const {
    publicRuntimeConfig: { envVars },
  } = getConfig();
  const hotjarEnabledCheck = envVars.HOTJAR_ENABLED === 'true';

  useEffect(() => {
    if (hotjarEnabledCheck && !ssrRender) {
      hotjar.initialize(envVars.HOTJAR_SITE_ID, 6);
    }
  }, []);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Eagle Share',
    url: 'https://www.eagleshare.com/',
    logo: 'https://www.eagleshare.com/imgs/es/og_es_logo.jpg',
    description: seoValues?.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '11860 S. La Cienega Blvd. Hawthorne, California, 90250',
      addressLocality: 'Los Angeles',
      addressRegion: 'California',
      addressCountry: 'United States',
    },
    sameAs: ['https://www.instagram.com/eagleshare/'],

  };

  return (
    <>
      <Head>
        {seoValues && generateMetaData(seoValues)}
        {!process.browser && (
          <>
            <link rel="canonical" href={headLinks.canonical} />
            {headLinks.urls.map((local) => (
              <link
                key={local.hreflang}
                rel="alternate"
                href={local.url}
                hrefLang={local.hreflang}
              />
            ))}
          </>
        )}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
      <Box className={classes.bannerContainer}>
        <WelcomeBanner i18n={t} />
      </Box>
      <Box className={classes.pageContainer}>
        {/* <LazyLoad ssrRender={ssrRender}>
          <Box className={classes.insuranceContainer}>
            <InsuranceInfo i18n={t} />
          </Box>
        </LazyLoad> */}
        <LazyLoad ssrRender={ssrRender}>
          <Box className={classes.howRentingWorksContainer}>
            <HowRentingWorks i18n={t} />
          </Box>
        </LazyLoad>
        <LazyLoad ssrRender={ssrRender}>
          <QuestionsSection i18n={t} />
        </LazyLoad>
        <LazyLoad ssrRender={ssrRender}>
          <GotYourBack i18n={t} />
        </LazyLoad>
        <LazyLoad ssrRender={ssrRender}>
          <WhyEagleShare i18n={t} />
        </LazyLoad>
      </Box>
      <LazyLoad ssrRender={ssrRender}>
        <ReadyToEarn i18n={t} />
      </LazyLoad>
    </>
  );
};

ListYourBike.getInitialProps = async (ctx) => {
  let seoValues;
  const params = { preferred_seo_identifer: '/rent-my-motorcycle' };
  const ssrRender = ctx.req ? isCrawlers(ctx.req.headers['user-agent']) : false;
  if (ssrRender) {
    const { success, data } = await serverFetch(ctx, API.seoValues, params);

    if (success && data) {
      seoValues = data;
    }
  }
  // generate canonical and hrefLang
  const host = ctx.req ? ctx.req.headers.host : window.location.host;
  let url;
  ctx.req ? (url = ctx.req.url) : (url = ctx.asPath);
  const headLinks = generateHreflang(url, '/rent-my-motorcycle', host);
  if (ctx.req) {
    const redirectLink = headLinks.urls.find((item) => {
      if (ctx.req.locale === 'en') {
        return item.hreflang === 'x-default';
      }
      return item.hreflang === ctx.req.locale;
    });
    if (!redirectLink.url.includes(decodeURI(ctx.req.url))) {
      ctx.res.writeHead(308, {
        Location: encodeURI(redirectLink.url),
      });
      ctx.res.end();
    }
  }

  return ({
    namespacesRequired: [
      'fe_list_your_bike',
      'fe_es_how_renting_works',
      'fe_es_questions_section',
      'fe_es_got_your_back',
      'fe_es_why_eagleShare',
      'fe_es_insurance_info_component',
    ],
    seoValues,
    headLinks,
    pendingOnLayout: false,
    pageIdentifier: 'rent-my-motorcycle',
    ssrRender,
  });
};
export default withTranslation([
  'fe_list_your_bike',
  'fe_es_how_renting_works',
  'fe_es_questions_section',
  'fe_es_got_your_back',
  'fe_es_why_eagleShare',
  'fe_es_insurance_info_component',
])(ListYourBike);
